// Google Analytics (the Google tag, gtag.js; docs/project/analytics-spec.md): Google's install
// snippet as TS, run under PostHog's rule — by web/site/site.ts once the cookie banner allows
// analytics, by web/src/main.tsx with the app. The id is the templated meta (config
// GA_MEASUREMENT_ID); empty = no tag. The snippet defines the queueing dataLayer and gtag, loads
// gtag.js async, and configures the property, whose own page_view covers this load; the SPA's
// pushState navigations are GA4's enhanced measurement (a property setting, on by default), not
// ours. No user id rides on it. Two events do, the ones Google Ads imports as conversions
// (featurette #155 follow-up, 2026-10-30; own-auth placement 2026-11-03): the funnel's lead as
// `generate_lead` (web/site/form.ts on /api/start's answer, web/site/login.ts on /api/auth/link's
// `{ next }` — beside the Reddit Lead) and its registration as `sign_up` (web/site/login.ts, on
// the consume's `verified`). Google's recommended names, so GA4 and Ads know them; no PII in
// their parameters. Each is fired by the page the browser navigates to next, not the one that
// learned of it: gtag holds hits in a batch for seconds, and a document torn down first sends
// nothing (measured 2026-11-03; analytics-spec). The event waits in sessionStorage, and
// startGoogleAnalytics on the next page sends it once the tag is up.

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Loads the Google tag and configures the property; idempotent, no-op without an id. */
export function startGoogleAnalytics(): void {
  const id = document.querySelector('meta[name="featurette-ga"]')?.getAttribute("content")?.trim();
  if (!id || window.gtag) return;
  window.dataLayer ??= [];
  const dataLayer = window.dataLayer;
  // gtag.js reads `arguments` objects off the layer, not arrays: the snippet's own gtag().
  function gtag(..._args: unknown[]): void {
    // biome-ignore lint/complexity/noArguments: what gtag.js expects on the layer
    dataLayer.push(arguments);
  }
  window.gtag = gtag;
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.async = true;
  document.head.append(script);
  gtag("js", new Date());
  // A local run (the local prod stack, dev with an id set) shows in the property's DebugView.
  gtag("config", id, location.hostname === "localhost" ? { debug_mode: true } : {});
  sendPending(gtag);
}

/** Where an event waits for the next page (sessionStorage: this tab, same origin). */
const PENDING_KEY = "featurette-ga-pending";
/** Older than this, a pending event is dropped: it belongs to a visit that ended. */
export const PENDING_TTL_MS = 60_000;

interface Pending {
  name: string;
  params: Record<string, string>;
  at: number;
}

function readPending(): Pending[] {
  try {
    const list: unknown = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? "[]");
    return Array.isArray(list) ? (list as Pending[]) : [];
  } catch {
    return [];
  }
}

/** Queues one event for the page the browser is about to load. Nothing (no throw) when storage
 * is unavailable: a conversion that could not be kept is not the page's problem. */
function queue(name: string, params: Record<string, string>): void {
  try {
    const list = readPending();
    list.push({ name, params, at: Date.now() });
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch {}
}

/** Sends what the previous page queued, in order, through `gtag`, and empties the queue; an
 * entry older than PENDING_TTL_MS is dropped. Returns the names sent. */
export function sendPending(gtag: (...args: unknown[]) => void, now = Date.now()): string[] {
  const list = readPending();
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {}
  const sent: string[] = [];
  for (const e of list) {
    if (now - e.at > PENDING_TTL_MS) continue;
    gtag("event", e.name, e.params);
    sent.push(e.name);
  }
  return sent;
}

/** The funnel's lead: an account made — the landing submit's request accepted, or a new address
 * on /login. Sent by the next page (the project, /projects). */
export function trackLead(): void {
  queue("generate_lead", { lead_source: "free_video_form" });
}

/** The funnel's registration: the first mailed link clicked — once per account, the server's rule
 * (auth/routes.ts consume, `verified`). `method` is how the visitor signed up: "email" today, an
 * OAuth provider's name once there is one. Sent by the next page (`next`). */
export function trackSignUp(method: string): void {
  queue("sign_up", { method });
}
